import * as THREE from "three";
import type { Game } from "./Game";

/**
 * A screen: field, battle, menu, title. Scenes live on a stack, so a battle can
 * be pushed over the field and pop back to it with the field untouched.
 */
export abstract class Scene {
  /** Assigned by the stack when the scene is pushed. */
  game!: Game;

  readonly scene = new THREE.Scene();
  camera: THREE.Camera = new THREE.PerspectiveCamera(60, 1, 0.1, 500);

  /**
   * False means the scene below keeps rendering underneath this one (menus,
   * pause screens). True means it fully covers what is behind it.
   */
  opaque = true;

  /** Called once when pushed. May be async — the stack awaits it. */
  async enter(): Promise<void> {}

  /** Called once when popped. Dispose GPU resources here. */
  exit(): void {}

  /** Something was pushed on top of this scene. */
  pause(): void {}

  /** The thing on top of this scene went away. */
  resume(): void {}

  /** Only the topmost scene is updated. */
  update(_dt: number): void {}

  resize(width: number, height: number): void {
    const cam = this.camera;
    if (cam instanceof THREE.PerspectiveCamera) {
      cam.aspect = width / height;
      cam.updateProjectionMatrix();
    }
  }

  render(renderer: THREE.WebGLRenderer): void {
    renderer.render(this.scene, this.camera);
  }

  /** Recursively free geometries/materials owned by this scene's graph. */
  protected disposeGraph(root: THREE.Object3D = this.scene): void {
    root.traverse((obj) => {
      const mesh = obj as Partial<THREE.Mesh>;
      mesh.geometry?.dispose();
      const mat = mesh.material;
      if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
      else mat?.dispose();
    });
  }
}
